import { useMemo } from 'react';
import {
  useNavigate,
  useSearchParams,
  useParams,
  type NavigateOptions,
  type Path,
  useLocation,
} from 'react-router-dom';
import useAppConfig from 'canvas_states/config/useAppConfig';
import useUrlInfo from '../utils/urls/useUrlInfo';
import commonConfig from '../configs/commonConfig';
import type { Configuration } from '../../../types/Configuration';
import usePreviousPageUrl from '../utils/urls/usePreviousPageUrl';
import ErrorStatus from '../../../types/errors/ErrorStatus';
import type ErrorReason from '../../../types/errors/ErrorReason';
import useCurrentLanguage from 'canvas_states/language/useCurrentLanguage';
import useCurrentCurrency from 'canvas_states/currency/useCurrentCurrency';

/**
 * Sometimes the slider tooltips stay visible when moving to payment page. @see https://plusgrade.jira.com/browse/FE-134
 * Seems to be a MUI bug: (@see https://stackoverflow.com/questions/66780816/material-ui-tooltip-not-been-removed-when-the-origin-element-had-been-removed)
 * This is the hacky way to fix this bug.
 */
const fixTooltipBugWhenChangingThePage = () => {
  try {
    document.querySelectorAll('[role="tooltip"]').forEach((item) => {
      (item as HTMLDivElement).style.display = 'none';
    });
  } catch {}
};

const isEmpty = (value: unknown) => value === '' || value == null;

/**
 * Converts a record of parameters to a string of parameters. If the value is null, undefined or empty string,
 * it will not be included in the final string.
 * @param params
 * @returns string
 */
const toSearchParamsString = (params: Record<string, string>) => {
  const value = new URLSearchParams(Object.entries(params).filter((param) => !isEmpty(param[1]))).toString();

  return value ? `?${value}` : '';
};

/**
 * Due to how Canvas handles apps and the nature of LABS projects, we have decided to add a key to the partnerConfig called 'labsPartnerType'
 * This key has a namespace 'labs' and shows the type of partner in the context of the LABS team
 *
 * i.e labs:priorityFlight
 * i.e labs:speedPass (future to come)
 *
 * This should reflect in the routing process as the url contains 'labs' as a namespace:
 * i.e /labs/priorityflight/
 *
 * @param config
 * @returns string | undefined
 */
const getPartnerType = (config: Pick<Configuration, 'partnerType' | 'labsPartnerType'>) =>
  config.labsPartnerType ? config.labsPartnerType.toLowerCase().replace(':', '/') : config.partnerType;

const PREVIOUS_OFFER_IDS = 'previousOfferIds';
const OFFER_IDS = 'offerIds';
const RETRYABLE_OFFER_IDS = 'retryableOfferIds';
const STATUS = 'status';
const REASON = 'reason';
const CALLBACK_URL = 'callbackUrl';

const useAppRouter = () => {
  const navigate = useNavigate();
  const config = useAppConfig().states.config;
  const partnerType = getPartnerType(config);
  const { pid, requestId } = useUrlInfo().states;
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { currentLanguage } = useCurrentLanguage().states;
  const { currentCurrency } = useCurrentCurrency().states;

  const { offerId = '' } = useParams();
  const formattedSearchParams = (symbol = '&') =>
    searchParams.toString() ? `${symbol}${searchParams.toString()}` : '';

  const {
    actions: { setPreviousPageUrlWithCurrentPageUrl },
    selectors: { getPreviousPageUrl },
  } = usePreviousPageUrl();

  return useMemo(() => {
    const paymentRoute = `/payment/partner/${pid}/${requestId}`;

    const navigateToOffersRoute = (newRequestId: string = requestId) =>
      navigate(`/offers/${partnerType}/partner/${pid}/${newRequestId}`);

    const callbackUrl = decodeURIComponent(searchParams.get(CALLBACK_URL) ?? '');

    return {
      actions: {
        navigateToPaymentRoute: ({ search }: Partial<Path> = {}) => {
          fixTooltipBugWhenChangingThePage();
          setPreviousPageUrlWithCurrentPageUrl();
          navigate({
            pathname: paymentRoute,
            search: `${search ?? ''}${formattedSearchParams(search ? '&' : '?')}`,
          });
        },
        navigateToSSMP: (offerId: string) => {
          navigate(`/offers/${partnerType}/partner/${pid}/${requestId}/${offerId}/modify`);
        },
        navigateToOutcomeRoute: (status: string) =>
          navigate({
            pathname: `/payment/partner/${pid}/${requestId}/card-decline/outcome`,
            search: `?${STATUS}=${status}`,
          }),
        navigateToOffersRoute,
        navigateToSubmissionRoute: (offerIds?: string[], retryableOfferIds?: string[], options?: NavigateOptions) => {
          navigate(
            {
              pathname: `/submission/partner/${pid}/${requestId}`,
              search:
                offerIds && retryableOfferIds
                  ? `?${OFFER_IDS}=${offerIds.join(',')}&${RETRYABLE_OFFER_IDS}=${retryableOfferIds.join(',')}${formattedSearchParams()}`
                  : formattedSearchParams('?'),
            },
            options,
          );
        },
        navigateToEcommerceCallbackRoute: (
          offerIds?: string[],
          retryableOfferIds?: string[],
          options?: NavigateOptions,
        ) => {
          navigate(
            {
              pathname: `/payment/partner/${pid}/${requestId}/ecommerce/callback`,
              search:
                offerIds && retryableOfferIds
                  ? `?${OFFER_IDS}=${offerIds.join(',')}&${RETRYABLE_OFFER_IDS}=${retryableOfferIds.join(
                      ',',
                    )}&${searchParams.toString()}`
                  : '',
            },
            options,
          );
        },
        generateCreditCardDeclineThreeDSCallbackRoute: (offerId: string, updateAllBids: boolean) =>
          `${window.location.origin}/payment/partner/${pid}/${requestId}/card-decline/callback?offerId=${offerId}&updateAllBids=${updateAllBids}`,
        generateThreeDSCallbackUrl: () => {
          const params = toSearchParamsString({
            lang: currentLanguage?.code,
            currency: currentCurrency?.code,
            [CALLBACK_URL]: callbackUrl,
          });

          return `${window.location.origin}/payment/partner/${pid}/${requestId}/callback${params}`;
        },
        generateEcommerceThreeDSCallbackUrl: (previousOfferIds: string[] = []) => {
          const params = toSearchParamsString({
            [PREVIOUS_OFFER_IDS]: previousOfferIds.join(','),
            lang: currentLanguage?.code,
            currency: currentCurrency?.code,
            [CALLBACK_URL]: callbackUrl,
          });

          return `${window.location.origin}/payment/partner/${pid}/${requestId}/ecommerce/callback${params}`;
        },
        generateEcommerceThreeDSChallengeUrl: () => commonConfig.ecommerceThreeDSRedirectionUrl,
        generateThreeDSChallengeUrl: () => commonConfig.threeDSRedirectionUrl,
        navigateToConfirmationRoute: (offerIds?: string[]) => {
          navigate({
            pathname: `/confirmation/partner/${pid}/${requestId}`,
            search: offerIds
              ? `?${OFFER_IDS}=${offerIds.join(',')}${formattedSearchParams()}`
              : formattedSearchParams('?'),
          });
        },
        navigateTo404Route: () => navigate(`/partner/${pid}/404`),
        navigateToErrorRoute: (status: ErrorStatus, reason?: ErrorReason) => {
          const reasonQuery = reason === undefined ? '' : `&${REASON}=${reason}`;
          /**
           * we don't want to show the search params that contain the callbackURL for the WCI flow
           * as we don't want to show the top banner in this specific case, also we don't want to
           * show the mvToken for the POINTS SSO.
           * Hence, we don't include the search params for this specific error page flow
           */
          if (
            status === ErrorStatus.WCI_CALLBACK_URL_VALIDATION_ERROR ||
            status === ErrorStatus.LOYALTY_TOKEN_LOGIN_ERROR ||
            status === ErrorStatus.LOYALTY_ACCOUNT_DETAIL_ERROR
          ) {
            navigate({
              pathname: `/error/partner/${pid}/${requestId}`,
              search: `?${STATUS}=${status}${reasonQuery}`,
            });
          } else {
            navigate({
              pathname: `/error/partner/${pid}/${requestId}`,
              search: `?${STATUS}=${status}${reasonQuery}${formattedSearchParams()}`,
            });
          }
        },
        navigateToPreviousRoute: () => {
          const url = new URL(getPreviousPageUrl());
          const { pathname, search } = url;

          if (url) {
            navigate({
              pathname,
              search,
            }); // since the url is absolute is need to use this hack.
          } else {
            navigateToOffersRoute();
          }
        },
      },
      states: {
        ssmpOfferId: offerId,
        creditCardDeclineOfferId: offerId,
        previousOfferIds: searchParams.get(PREVIOUS_OFFER_IDS)?.split(',') ?? [], // for 3ds validation page
        offerIds: searchParams.get(OFFER_IDS)?.split(',') ?? [],
        retryableOfferIds: searchParams.get(RETRYABLE_OFFER_IDS)?.split(',') ?? [],
        status: searchParams.get(STATUS) ?? '',
        reason: searchParams.get(REASON) ?? '',
        callbackUrl,
        isConfirmationRoute: location.pathname.includes('/confirmation'),
        lastName: searchParams.get('lastName') ?? 'PLUSGRADELAST',
      },
    };
  }, [navigate, pid, requestId, partnerType, offerId, searchParams]);
};

export default useAppRouter;
